import React, { useState } from 'react';
import { Container, Card, Table, Row, Col, Button, Badge, Pagination, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import { FaSearch, FaFileExcel } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import * as api from '~/Services/Service';
import { strParaMonetario, salvarEmExcel, validarCPF, validarCNPJ } from '~/Utils/Helpers';
import CardResumoGeral from '~/Components/ConsultaCPF/CardResumoGeral';
import CardIndicadores from '~/Components/ConsultaCPF/CardIndicadores';
import TabelaParcelasCard from '~/Components/ConsultaCPF/TabelaParcelasCard';
import CardCobranca from '~/Components/ConsultaCPF/CardCobranca';
import AlertasPanel from '~/Components/ConsultaCPF/AlertasPanel';
import ModalNegativacao from '~/Components/ConsultaCPF/ModalNegativacao';

const formatarPerc = (perc) => {
  if (perc == null) return '0,00%';
  const num = Number(perc);
  if (isNaN(num)) return '0,00%';
  return `${num.toFixed(2).replace('.', ',')}%`;
};

const formatarCPF = (cpf) => {
  if (cpf == null) return '—';
  const alfanumerico = String(cpf).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (/[A-Z]/.test(alfanumerico) || alfanumerico.length > 11) {
    return alfanumerico
      .padStart(14, '0')
      .replace(/^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  return alfanumerico.padStart(11, '0').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

// DW-0963: validarCNPJ alfanumérico importado de ~/Utils/Helpers (fonte única; validador local removido).

const aplicarMascaraDocumento = (value) => {
  // DW-0963: preserva CNPJ alfanumérico (12 alfanuméricos + 2 DV). CPF (≤11) permanece numérico.
  const doc = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 14);
  const ehCnpj = /[A-Z]/.test(doc) || doc.length > 11;

  if (!ehCnpj) {
    return doc
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
  }

  let out = doc.slice(0, 2);
  if (doc.length > 2) out += '.' + doc.slice(2, 5);
  if (doc.length > 5) out += '.' + doc.slice(5, 8);
  if (doc.length > 8) out += '/' + doc.slice(8, 12);
  if (doc.length > 12) out += '-' + doc.slice(12, 14);
  return out;
};

const documentoValido = (documento) => {
  // DW-0963: CNPJ alfanumérico (14 posições) decidido por comprimento alfanumérico.
  const alfanumerico = String(documento || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (alfanumerico.length === 14) return validarCNPJ(documento);
  const numeros = documento.replace(/\D/g, '');
  if (numeros.length === 11) return validarCPF(numeros);
  return false;
};

const normalizarDocumentoConsulta = (documento) => {
  const alfanumerico = String(documento || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (/[A-Z]/.test(alfanumerico) || alfanumerico.length > 11) return alfanumerico;
  return String(documento || '').replace(/\D/g, '');
};

const ConsultaCPF = () => {
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(false);
  const [lotes, setLotes] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [financeiro, setFinanceiro] = useState(null);
  const [cobranca, setCobranca] = useState(null);
  const [alertas, setAlertas] = useState(null);
  const [socios, setSocios] = useState([]);
  const [outrosTitulos, setOutrosTitulos] = useState(null);
  const [negativacaoStatus, setNegativacaoStatus] = useState(null);
  const [boletoSelecionado, setBoletoSelecionado] = useState(null);
  const [modalNegativacao, setModalNegativacao] = useState(false);
  const [todosBoletosCSV, setTodosBoletosCSV] = useState('');
  const [sociosPagina, setSociosPagina] = useState(1);
  const [exportando, setExportando] = useState(false);
  const SOCIOS_POR_PAGINA = 10;

  // Fix #2: Filtro por boleto filtra grid e recalcula resumo (fiel ao Delphi unicobol + BconsultaClick)
  const lotesExibidos = boletoSelecionado
    ? lotes.filter((l) => l.codigo_boleto === boletoSelecionado)
    : lotes;

  const calcularResumo = (lotesArr) => ({
    adquiridos: lotesArr.length,
    rescindidos: lotesArr.filter((l) => l.situacao && l.situacao.startsWith('R')).length,
    quitados: lotesArr.filter((l) => l.situacao && l.situacao.startsWith('Q')).length,
    financiados: lotesArr.filter((l) => l.situacao && l.situacao.startsWith('F')).length,
    transferidos: lotesArr.filter((l) => l.situacao && l.situacao.startsWith('T')).length,
  });

  const resumoExibido = boletoSelecionado ? calcularResumo(lotesExibidos) : resumo;

  const carregarDadosBoletos = async (boletosCSV) => {
    const [financeiroResp, cobrancaResp, alertasResp, sociosResp] =
      await Promise.all([
        api.consultaCPFFinanceiro(boletosCSV),
        api.consultaCPFCobranca(boletosCSV),
        api.consultaCPFAlertas(boletosCSV),
        api.consultaCPFSocios(boletosCSV),
      ]);
    setFinanceiro(financeiroResp);
    setCobranca(cobrancaResp);
    setAlertas(alertasResp);
    setSocios(Array.isArray(sociosResp) ? sociosResp : []);
  };

  const carregarDadosCpf = async (cpfNumerico) => {
    const [outrosResp, negStatusResp] = await Promise.all([
      api.consultaCPFOutrosTitulos(cpfNumerico),
      api.consultaCPFNegativacaoStatus(cpfNumerico),
    ]);
    setOutrosTitulos(outrosResp);
    setNegativacaoStatus(negStatusResp);
  };

  // Fix #1: lógica de consulta extraída para reuso (handleConsultar + handleClicarSocio)
  const consultarCpf = async (documento) => {
    if (!documento) {
      toast.error('Dados inválidos! Informe o CPF.');
      return;
    }

    if (![11, 14].includes(documento.length)) {
      toast.error('Informe um CPF com 11 dígitos ou CNPJ com 14 dígitos.');
      return;
    }

    setLoading(true);
    setBoletoSelecionado(null);
    setSociosPagina(1);
    try {
      const checarDigito = await api.checaCgcCpf();
      if (checarDigito === true && !documentoValido(documento)) {
        toast.error('Número digitado não representa CPF/CNPJ válido.');
        return;
      }

      const resp = await api.consultaCPFLotes(documento);
      const lotesData = resp.lotes != null ? resp.lotes : [];
      setLotes(lotesData);
      setResumo(resp.resumo != null ? resp.resumo : null);

      if (lotesData.length === 0) {
        toast.info('Nenhum registro encontrado.');
        setLoading(false);
        return;
      }

      const boletosCSV = lotesData.map((l) => l.codigo_boleto).join(',');
      setTodosBoletosCSV(boletosCSV);
      await Promise.all([
        carregarDadosBoletos(boletosCSV),
        carregarDadosCpf(documento),
      ]);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao carregar dados';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleConsultar = () => {
    const documento = normalizarDocumentoConsulta(cpf);
    consultarCpf(documento);
  };

  const handleFiltrarBoleto = async (codigoBoleto) => {
    setBoletoSelecionado(codigoBoleto);
    setLoading(true);
    try {
      await carregarDadosBoletos(String(codigoBoleto));
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao filtrar boleto';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleConsultaGeral = async () => {
    if (!todosBoletosCSV) return;
    setBoletoSelecionado(null);
    setLoading(true);
    try {
      await carregarDadosBoletos(todosBoletosCSV);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao recarregar dados';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLimpar = () => {
    setCpf('');
    setLotes([]);
    setResumo(null);
    setFinanceiro(null);
    setCobranca(null);
    setAlertas(null);
    setSocios([]);
    setOutrosTitulos(null);
    setNegativacaoStatus(null);
    setBoletoSelecionado(null);
    setTodosBoletosCSV('');
    setSociosPagina(1);
  };

  // Fix #5: Fiel ao Delphi BitBtn51Click — modal abre para todos (histórico visível),
  // permissão 388 verificada apenas no botão "Incluir" dentro do modal
  const handleAbrirNegativacao = () => {
    setModalNegativacao(true);
  };

  const handleSalvarNegativacao = async () => {
    const cpfNumerico = normalizarDocumentoConsulta(cpf);
    try {
      const negStatusResp = await api.consultaCPFNegativacaoStatus(cpfNumerico);
      setNegativacaoStatus(negStatusResp);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao atualizar status de negativação';
      toast.error(msg);
    }
  };

  // Fix #1: Fiel ao Delphi RxDBGrid2DblClick — seta CPF e auto-consulta
  const handleClicarSocio = (cpfSocio) => {
    if (cpfSocio == null || cpfSocio === 0) return;
    const cpfStr = normalizarDocumentoConsulta(cpfSocio);
    if (!cpfStr) return;
    setCpf(formatarCPF(cpfStr));
    consultarCpf(cpfStr);
  };

  const handleExportarExcel = async () => {
    setExportando(true);
    try {
    const cpfNumerico = normalizarDocumentoConsulta(cpf);
    const formatarDataExcel = (data) => {
      if (!data) return '';
      const m = moment(data);
      return m.isValid() ? m.format('DD/MM/YYYY') : '';
    };
    const formatarValor = (val) => {
      if (val == null) return 0;
      const num = Number(val);
      return isNaN(num) ? 0 : num;
    };

    const baixadasLinhas = financeiro && financeiro.baixadas ? [
      { categoria: 'Total Pago', parcelas: financeiro.baixadas.total_parcelas || 0, percentual_parcelas: financeiro.baixadas.percentual_total || '0.00', valor: formatarValor(financeiro.baixadas.total_valor), percentual_valor: financeiro.baixadas.percentual_valor_total || '0.00' },
      { categoria: 'Em Dia', parcelas: financeiro.baixadas.em_dia_parcelas || 0, percentual_parcelas: financeiro.baixadas.percentual_em_dia || '0.00', valor: formatarValor(financeiro.baixadas.em_dia_valor), percentual_valor: financeiro.baixadas.percentual_valor_em_dia || '0.00' },
      { categoria: 'Em Atraso', parcelas: financeiro.baixadas.em_atraso_parcelas || 0, percentual_parcelas: financeiro.baixadas.percentual_em_atraso || '0.00', valor: formatarValor(financeiro.baixadas.em_atraso_valor), percentual_valor: financeiro.baixadas.percentual_valor_em_atraso || '0.00' },
    ] : [];

    const emAbertoLinhas = financeiro && financeiro.em_aberto ? [
      { categoria: 'Total', parcelas: financeiro.em_aberto.total_parcelas || 0, percentual_parcelas: financeiro.em_aberto.percentual_total || '0.00', valor: formatarValor(financeiro.em_aberto.total_valor), percentual_valor: financeiro.em_aberto.percentual_valor_total || '0.00' },
      { categoria: 'Vencidas', parcelas: financeiro.em_aberto.vencidas_parcelas || 0, percentual_parcelas: financeiro.em_aberto.percentual_vencidas || '0.00', valor: formatarValor(financeiro.em_aberto.vencidas_valor), percentual_valor: financeiro.em_aberto.percentual_valor_vencidas || '0.00' },
      { categoria: 'A Vencer', parcelas: financeiro.em_aberto.a_vencer_parcelas || 0, percentual_parcelas: financeiro.em_aberto.percentual_a_vencer || '0.00', valor: formatarValor(financeiro.em_aberto.a_vencer_valor), percentual_valor: financeiro.em_aberto.percentual_valor_a_vencer || '0.00' },
    ] : [];

    const indicadoresLinhas = financeiro ? [
      { indicador: 'Maior atraso atual (dias)', valor: String(financeiro.em_aberto ? financeiro.em_aberto.maior_atraso_atual_dias || 0 : 0) },
      { indicador: 'Maior atraso pago (dias)', valor: String(financeiro.baixadas ? financeiro.baixadas.maior_atraso_pago_dias || 0 : 0) },
      { indicador: 'Média atraso pago (dias)', valor: String(financeiro.baixadas ? financeiro.baixadas.media_atraso_dias || 0 : 0) },
      { indicador: 'Vlr médio parc. aberta', valor: financeiro.em_aberto ? strParaMonetario(financeiro.em_aberto.valor_medio_parcela, false) : '0,00' },
    ] : [];

    const cobrancaLinhas = cobranca ? [
      { tipo: 'Negativações', quantidade: (cobranca.negativacoes ? cobranca.negativacoes.quantidade : 0) || 0, ultima_data: formatarDataExcel(cobranca.negativacoes ? cobranca.negativacoes.ultima_data : null) },
      { tipo: 'Ref. Parcial', quantidade: (cobranca.refinanciamento_parcial ? cobranca.refinanciamento_parcial.quantidade : 0) || 0, ultima_data: formatarDataExcel(cobranca.refinanciamento_parcial ? cobranca.refinanciamento_parcial.ultima_data : null) },
      { tipo: 'Ref. Geral', quantidade: (cobranca.refinanciamento_geral ? cobranca.refinanciamento_geral.quantidade : 0) || 0, ultima_data: formatarDataExcel(cobranca.refinanciamento_geral ? cobranca.refinanciamento_geral.ultima_data : null) },
      { tipo: 'Tribunal Arbitral', quantidade: (cobranca.tribunal_arbitral ? cobranca.tribunal_arbitral.quantidade : 0) || 0, ultima_data: formatarDataExcel(cobranca.tribunal_arbitral ? cobranca.tribunal_arbitral.ultima_data : null) },
      { tipo: 'Not. Extrajudicial', quantidade: (cobranca.notificacao_extrajudicial ? cobranca.notificacao_extrajudicial.quantidade : 0) || 0, ultima_data: formatarDataExcel(cobranca.notificacao_extrajudicial ? cobranca.notificacao_extrajudicial.ultima_data : null) },
      { tipo: 'Judicial', quantidade: (cobranca.judicial ? cobranca.judicial.quantidade : 0) || 0, ultima_data: formatarDataExcel(cobranca.judicial ? cobranca.judicial.ultima_data : null) },
      { tipo: 'Devoluções', quantidade: (cobranca.devolucoes ? cobranca.devolucoes.quantidade : 0) || 0, ultima_data: formatarDataExcel(cobranca.devolucoes ? cobranca.devolucoes.ultima_data : null) },
    ] : [];

    const sociosLinhas = socios.map((s) => ({
      cpf: formatarCPF(s.cpf),
      nome: s.nome,
      tipo: s.tipo,
      status: s.status,
    }));

    const outrosTitulosLinhas = outrosTitulos && outrosTitulos.visivel ? [
      { categoria: 'Pagos', parcelas: outrosTitulos.pagos_parcelas || 0, valor: formatarValor(outrosTitulos.pagos_valor), percentual: '' },
      { categoria: 'Pagos em Atraso', parcelas: outrosTitulos.atraso_parcelas || 0, valor: formatarValor(outrosTitulos.atraso_valor), percentual: outrosTitulos.atraso_percentual || '0.00' },
      { categoria: 'Em Aberto', parcelas: outrosTitulos.aberto_parcelas || 0, valor: formatarValor(outrosTitulos.aberto_valor), percentual: '' },
      { categoria: 'Vencidos', parcelas: outrosTitulos.vencidos_parcelas || 0, valor: formatarValor(outrosTitulos.vencidos_valor), percentual: outrosTitulos.vencidos_percentual || '0.00' },
    ] : [];

    const payload = {
      cpf: cpfNumerico,
      lotes: lotesExibidos.map((item) => ({
        codigo_grupo_empresa: item.codigo_grupo_empresa,
        nome_da_familia: item.nome_da_familia,
        codigo_boleto: item.codigo_boleto,
        mapa1: item.mapa1,
        mapa2: item.mapa2,
        area_total: item.area_total || 0,
        data_proposta: formatarDataExcel(item.data_proposta),
        situacao: item.situacao,
        conta_cedente: item.conta_cedente,
        apelido_conta: item.apelido_conta,
        cobranca: item.cobranca ? String(item.cobranca) : '',
        corretor: item.corretor || '',
      })),
      baixadas: baixadasLinhas,
      em_aberto: emAbertoLinhas,
      indicadores: indicadoresLinhas,
      cobranca: cobrancaLinhas,
      socios: sociosLinhas,
      outros_titulos: outrosTitulosLinhas,
    };

    await salvarEmExcel(payload, '/exportador/excel/consulta-cpf');
    } catch (error) {
      toast.error('Erro ao exportar Excel.');
    } finally {
      setExportando(false);
    }
  };

  const getNegativacaoVariant = () => {
    if (!negativacaoStatus) return 'success';
    if (negativacaoStatus.cor === 'vermelho') return 'danger';
    if (negativacaoStatus.cor === 'laranja') return 'warning';
    return 'success';
  };

  const getNegativacaoLabel = () => {
    if (!negativacaoStatus) return 'SEM REGISTRO';
    if (negativacaoStatus.status === 'incluido') return 'INCLUÍDO';
    if (negativacaoStatus.status === 'excluido') return 'EXCLUÍDO';
    return 'SEM REGISTRO';
  };

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Consulta CPF" />

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row>
            <Col xs={12} md={3}>
              <label>CPF / CNPJ</label>
              <input
                type="text"
                className="form-control"
                value={cpf}
                onChange={(e) => {
                  setCpf(aplicarMascaraDocumento(e.target.value));
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleConsultar()}
                placeholder="Digite o CPF..."
                maxLength={18}
              />
            </Col>
            <Col xs={12} md={6} className="d-flex align-items-end mt-2 mt-md-0">
              <Button variant="primary" onClick={handleConsultar} className="mr-2 btn-sm">
                <FaSearch className="mr-1" /> Consultar
              </Button>
              <Button
                variant="outline-secondary"
                onClick={handleConsultaGeral}
                disabled={lotes.length === 0}
                className="mr-2 btn-sm"
              >
                Consulta Geral
              </Button>
              <Button variant="outline-secondary" onClick={handleLimpar} className="mr-2 btn-sm">
                Limpar
              </Button>
              <Button
                variant="outline-success"
                onClick={handleExportarExcel}
                disabled={lotes.length === 0 || exportando}
                className="btn-sm"
              >
                {exportando ? (
                  <><Spinner as="span" animation="border" size="sm" className="mr-1" /> Exportando...</>
                ) : (
                  <><FaFileExcel className="mr-1" /> Excel</>
                )}
              </Button>
            </Col>
            <Col xs={12} md={3} className="d-flex align-items-end justify-content-md-end mt-2 mt-md-0">
              {negativacaoStatus && (
                <Badge
                  variant={getNegativacaoVariant()}
                  className="mr-2 p-2"
                  style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                  onClick={handleAbrirNegativacao}
                  title="Clique para gerenciar negativação"
                >
                  Negativação: {getNegativacaoLabel()}
                </Badge>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <Loading />
      ) : (
        <>
          {resumoExibido && <CardResumoGeral resumo={resumoExibido} />}

          {alertas && <AlertasPanel alertas={alertas} />}

          {lotesExibidos.length > 0 && (
            <Card className="shadow-sm mb-3">
              <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
                <strong>Lotes / Boletos</strong>
                {boletoSelecionado && (
                  <span className="ml-2">(filtrado: boleto {boletoSelecionado})</span>
                )}
              </Card.Header>
              <Card.Body className="p-0">
                {/* Desktop */}
                <div className="d-none d-md-block" style={{ overflowX: 'auto' }}>
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>GE</th>
                        <th>Loteamento</th>
                        <th>Boleto</th>
                        <th>QD</th>
                        <th>LT</th>
                        <th>Área</th>
                        <th>Data</th>
                        <th>Situação</th>
                        <th>Cliente</th>
                        <th>Apelido</th>
                        <th>Cobrança</th>
                        <th>Corretor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lotesExibidos.map((item) => (
                        <tr
                          key={item.codigo_boleto}
                          onDoubleClick={() => handleFiltrarBoleto(item.codigo_boleto)}
                          style={{
                            cursor: 'pointer',
                            backgroundColor:
                              boletoSelecionado === item.codigo_boleto
                                ? '#cce5ff'
                                : undefined,
                          }}
                          title="Duplo clique para filtrar por este boleto"
                        >
                          <td>{item.codigo_grupo_empresa}</td>
                          <td>{item.nome_da_familia}</td>
                          <td>{item.codigo_boleto}</td>
                          <td>{item.mapa1}</td>
                          <td>{item.mapa2}</td>
                          <td>{item.area_total}</td>
                          <td>
                            {item.data_proposta
                              ? moment(item.data_proposta).format('DD/MM/YYYY')
                              : '—'}
                          </td>
                          <td>{item.situacao}</td>
                          <td>{item.conta_cedente}</td>
                          <td>{item.apelido_conta}</td>
                          <td>{item.cobranca}</td>
                          <td>{item.corretor}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {/* Mobile */}
                <div className="d-md-none">
                  {lotesExibidos.map((item) => (
                    <div
                      key={item.codigo_boleto}
                      className="border-bottom p-3"
                      onClick={() => handleFiltrarBoleto(item.codigo_boleto)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor:
                          boletoSelecionado === item.codigo_boleto
                            ? '#cce5ff'
                            : undefined,
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong>{item.nome_da_familia}</strong>
                        <Badge variant={
                          item.situacao && item.situacao.startsWith('Q') ? 'success'
                          : item.situacao && item.situacao.startsWith('F') ? 'info'
                          : item.situacao && item.situacao.startsWith('R') ? 'danger'
                          : 'secondary'
                        }>
                          {item.situacao}
                        </Badge>
                      </div>
                      <Row>
                        <Col xs={4}>
                          <small className="text-muted">Boleto</small>
                          <div>{item.codigo_boleto}</div>
                        </Col>
                        <Col xs={4}>
                          <small className="text-muted">QD/LT</small>
                          <div>{item.mapa1} / {item.mapa2}</div>
                        </Col>
                        <Col xs={4}>
                          <small className="text-muted">Data</small>
                          <div>{item.data_proposta ? moment(item.data_proposta).format('DD/MM/YY') : '—'}</div>
                        </Col>
                      </Row>
                      <Row className="mt-1">
                        <Col xs={6}>
                          <small className="text-muted">Cliente</small>
                          <div className="text-truncate" style={{ fontSize: '0.85rem' }}>{item.apelido_conta}</div>
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted">Corretor</small>
                          <div className="text-truncate" style={{ fontSize: '0.85rem' }}>{item.corretor}</div>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}

          {financeiro && (
            <Row className="mb-3">
              <Col md={6}>
                <TabelaParcelasCard titulo="Baixadas" dados={financeiro.baixadas} />
              </Col>
              <Col md={6}>
                <TabelaParcelasCard titulo="Em Aberto" dados={financeiro.em_aberto} />
              </Col>
            </Row>
          )}

          <Row className="mb-3">
            {financeiro && (
              <Col md={6}>
                <CardIndicadores financeiro={financeiro} />
              </Col>
            )}
            {cobranca && (
              <Col md={6}>
                <CardCobranca cobranca={cobranca} />
              </Col>
            )}
          </Row>

          {socios.length > 0 && (() => {
            const totalPaginas = Math.ceil(socios.length / SOCIOS_POR_PAGINA);
            const inicio = (sociosPagina - 1) * SOCIOS_POR_PAGINA;
            const sociosPaginados = socios.slice(inicio, inicio + SOCIOS_POR_PAGINA);

            const mudarPagina = (pagina) => {
              if (pagina < 1 || pagina > totalPaginas) return;
              setSociosPagina(pagina);
            };

            const maxBotoes = 5;
            const metade = Math.floor(maxBotoes / 2);
            let inicioPag = Math.max(1, sociosPagina - metade);
            let fimPag = Math.min(totalPaginas, inicioPag + maxBotoes - 1);
            if (fimPag - inicioPag + 1 < maxBotoes) {
              inicioPag = Math.max(1, fimPag - maxBotoes + 1);
            }

            const itensPaginacao = [];
            itensPaginacao.push(
              <Pagination.First key="first" onClick={() => mudarPagina(1)} disabled={sociosPagina === 1} />
            );
            itensPaginacao.push(
              <Pagination.Prev key="prev" onClick={() => mudarPagina(sociosPagina - 1)} disabled={sociosPagina === 1} />
            );
            if (inicioPag > 1) {
              itensPaginacao.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
            for (let n = inicioPag; n <= fimPag; n++) {
              itensPaginacao.push(
                <Pagination.Item key={n} active={n === sociosPagina} onClick={() => mudarPagina(n)}>
                  {n}
                </Pagination.Item>
              );
            }
            if (fimPag < totalPaginas) {
              itensPaginacao.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            itensPaginacao.push(
              <Pagination.Next key="next" onClick={() => mudarPagina(sociosPagina + 1)} disabled={sociosPagina === totalPaginas} />
            );
            itensPaginacao.push(
              <Pagination.Last key="last" onClick={() => mudarPagina(totalPaginas)} disabled={sociosPagina === totalPaginas} />
            );

            return (
              <Card className="shadow-sm mb-3">
                <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
                  <strong>Sócios / Pessoas Relacionadas</strong>
                </Card.Header>
                <Card.Body className="p-0">
                  {/* Desktop */}
                  <div className="d-none d-md-block">
                    <Table striped bordered hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>CPF</th>
                          <th>Nome</th>
                          <th>Tipo</th>
                          <th>Situação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sociosPaginados.map((s, idx) => (
                          <tr
                            key={`${s.cpf}-${inicio + idx}`}
                            onDoubleClick={() => handleClicarSocio(s.cpf)}
                            style={{ cursor: 'pointer' }}
                            title="Duplo clique para consultar este CPF"
                          >
                            <td>{formatarCPF(s.cpf)}</td>
                            <td>{s.nome}</td>
                            <td>{s.tipo}</td>
                            <td>{s.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {/* Mobile */}
                  <div className="d-md-none">
                    {sociosPaginados.map((s, idx) => (
                      <div
                        key={`${s.cpf}-${inicio + idx}`}
                        className="border-bottom p-3"
                        onClick={() => handleClicarSocio(s.cpf)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <strong>{s.nome}</strong>
                          <Badge variant={s.status === 'POSITIVO' ? 'danger' : 'success'}>
                            {s.status}
                          </Badge>
                        </div>
                        <Row>
                          <Col xs={7}>
                            <small className="text-muted">CPF</small>
                            <div style={{ fontSize: '0.85rem' }}>{formatarCPF(s.cpf)}</div>
                          </Col>
                          <Col xs={5}>
                            <small className="text-muted">Tipo</small>
                            <div style={{ fontSize: '0.85rem' }}>{s.tipo}</div>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                </Card.Body>
                {totalPaginas > 1 && (
                  <Card.Footer className="d-flex justify-content-between align-items-center py-2">
                    <small className="text-muted">
                      {inicio + 1} a {Math.min(inicio + SOCIOS_POR_PAGINA, socios.length)} de {socios.length} registros
                    </small>
                    <Pagination className="mb-0" size="sm">{itensPaginacao}</Pagination>
                  </Card.Footer>
                )}
              </Card>
            );
          })()}

          {outrosTitulos && outrosTitulos.visivel && (() => {
            const linhasOutros = [
              { label: 'Pagos', qtd: outrosTitulos.pagos_parcelas, valor: outrosTitulos.pagos_valor, perc: null },
              { label: 'Pagos em Atraso', qtd: outrosTitulos.atraso_parcelas, valor: outrosTitulos.atraso_valor, perc: outrosTitulos.atraso_percentual },
              { label: 'Em Aberto', qtd: outrosTitulos.aberto_parcelas, valor: outrosTitulos.aberto_valor, perc: null },
              { label: 'Vencidos', qtd: outrosTitulos.vencidos_parcelas, valor: outrosTitulos.vencidos_valor, perc: outrosTitulos.vencidos_percentual },
            ];
            return (
              <Card className="shadow-sm mb-3">
                <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
                  <strong>Outros Títulos</strong>
                </Card.Header>
                <Card.Body className="p-0 p-md-3">
                  {/* Desktop */}
                  <div className="d-none d-md-block">
                    <Table bordered size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Categoria</th>
                          <th className="text-right">Qtd. Parcelas</th>
                          <th className="text-right">Valor</th>
                          <th className="text-right">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhasOutros.map((l) => (
                          <tr key={l.label}>
                            <td>{l.label}</td>
                            <td className="text-right">{l.qtd != null ? l.qtd : 0}</td>
                            <td className="text-right">{strParaMonetario(l.valor, false)}</td>
                            <td className="text-right">{l.perc != null ? formatarPerc(l.perc) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  {/* Mobile */}
                  <div className="d-md-none">
                    {linhasOutros.map((l) => (
                      <div key={l.label} className="border-bottom p-2">
                        <div className="font-weight-bold mb-1">{l.label}</div>
                        <Row>
                          <Col xs={4}>
                            <small className="text-muted">Qtd</small>
                            <div>{l.qtd != null ? l.qtd : 0}</div>
                          </Col>
                          <Col xs={4}>
                            <small className="text-muted">Valor</small>
                            <div>{strParaMonetario(l.valor, false)}</div>
                          </Col>
                          <Col xs={4}>
                            <small className="text-muted">%</small>
                            <div>{l.perc != null ? formatarPerc(l.perc) : '—'}</div>
                          </Col>
                        </Row>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            );
          })()}
        </>
      )}

      {modalNegativacao && (
        <ModalNegativacao
          cpf={normalizarDocumentoConsulta(cpf)}
          show={modalNegativacao}
          onClose={() => setModalNegativacao(false)}
          onSave={handleSalvarNegativacao}
        />
      )}
    </Container>
  );
};

export default ConsultaCPF;
